import {useLocation, useNavigate} from "react-router-dom";
import {Alert, Anchor, Box, Center, CloseButton, Container, Group, LoadingOverlay, Paper, Space, Title} from "@mantine/core";
import {
  adjustToTimeZone,
  CompanyDisposalReportCriteria,
  getObjectId,
  getRequiredObjectId,
  useDownloadCompanyDisposalReport
} from "@ots-frontend/ots-data";
import {useTranslation} from "react-i18next";
import {useCallback, useEffect} from "react";
import {ThemedPage} from "../../components/page/ThemedPage/ThemedPage";
import {useApp} from "../../providers/RegionProvider/RegionProvider";
import {
  CompanyDisposalReportForm,
  CompanyDisposalReportFormValues
} from "../../components/form/CompanyDisposalReportForm/CompanyDisposalReportForm";
import {IconAlertCircle} from "@tabler/icons-react";
import {useScrollIntoView} from "@mantine/hooks";

type Props = {
  formDefaults?: Partial<CompanyDisposalReportFormValues>
}

export const CompanyDisposalReportFormPageRoute = () => {
  const {state} = useLocation();

  const formDefaults = state?.formDefaults;

  return (
    <CompanyDisposalReportFormPage formDefaults={formDefaults}/>
  );
};

export const CompanyDisposalReportFormPage = (props: Props) => {
    const {formDefaults} = props;

    const navigate = useNavigate();
    const {t} = useTranslation(["common"]);
    const {regionId, appCategory, baseQueryApi} = useApp()

    const {scrollIntoView: scrollErrorIntoView, targetRef: errorTargetRef} = useScrollIntoView<HTMLDivElement>({
      offset: 120,
      duration: 0,
    });

    const {scrollIntoView, targetRef} = useScrollIntoView<HTMLDivElement>({
      offset: 120,
      duration: 0,
    });

    const {
      downloadCompanyDisposalReport,
      report,
      error,
      loading
    } = useDownloadCompanyDisposalReport({baseQueryApi})

    const onFormSubmit = useCallback((formValues: CompanyDisposalReportFormValues) => {

      const freightTypeGroups = formValues.freightTypeGroups ?? []
      if (freightTypeGroups.length === 0) freightTypeGroups.push([])

      const criterias = freightTypeGroups.map((freightTypes) => ({
        fromDttm: adjustToTimeZone(formValues.fromDate).getTime(),
        toDttm: adjustToTimeZone(formValues.toDate).getTime(),
        regionId: regionId,
        rateModelId: getObjectId(formValues.rateModel),
        costModelId: 'FREIGHTCOST',
        operatorIdList: formValues.operator ? [getRequiredObjectId(formValues.operator)] : undefined,
        disposalCompanyId: getObjectId(formValues.disposalCompany),
        truckCompanyIdList: formValues.truckCompanies?.map(getRequiredObjectId),
        waterTypeIdList: freightTypes.map(getRequiredObjectId),
        siteIdList: formValues.sites?.map(getRequiredObjectId),
        includeTruckingData: formValues.includeTruckingData,
        includePipelineData: formValues.includePipelineData,
        pipelineFlowTimeGroup: formValues.pipelineFlowTimeGroup?.code,
        pipelineFlowAllocationType: formValues.pipelineFlowAllocationType?.code,
        createInvoice: formValues.createInvoices,
        invoiceNumber: formValues.invoiceNumber,
        invoiceGroupByList: formValues.invoiceGroupings?.map((ig) => ig.asTypeValue()),
        pickupWaterTypeSort:
          formValues.breakdownBy === 'pickup' && formValues.sortByFreightType,
        deliveryWaterTypeSort:
          formValues.breakdownBy === 'delivery' && formValues.sortByFreightType,
        startShiftReport: false,
        endShiftReport: false
      }))

      const criteria: CompanyDisposalReportCriteria = {
        criteriaList: criterias,
        sortByPickup: formValues.breakdownBy === 'pickup',
      }

      downloadCompanyDisposalReport(criteria)
    }, [regionId, downloadCompanyDisposalReport])

    useEffect(() => {
      if (report?.fileUrl) {
        scrollIntoView({alignment: 'start'})
      }
    }, [report?.fileUrl, scrollIntoView]);

    useEffect(() => {
      if (error) {
        scrollErrorIntoView({alignment: 'start'})
      }
    }, [error, scrollErrorIntoView]);

    return (
      <ThemedPage>
        {(classes) => (
          <Container className={classes.container} fluid={true} py="md">
            <Center>
              <Paper w="100%" shadow="sm" p="md" py="md">
                <Group position="apart">
                  <Title align="center">
                    {t("common.page.report.companydisposal.title", "Company Disposal Report", {ns: "common"})}
                  </Title>
                  <CloseButton title="Close" size="xl" iconSize={20} onClick={() => navigate(-1)}/>
                </Group>
                <Space h="md"/>
                {error &&
                  <>
                    <Alert ref={errorTargetRef} icon={<IconAlertCircle size="1rem"/>} title="Error" color="red">
                      {error?.data?.detail ?? t("common.error", "Error", {ns: "common"})}
                    </Alert>
                    <Space h="md"/>
                  </>
                }
                {report &&
                  <>
                    <Alert ref={targetRef} icon={<IconAlertCircle size="1rem"/>} title="Success" color="green">
                      <Anchor href={report.fileUrl} target={"_blank"}>
                        {t("common.action.download", "Download")}
                      </Anchor>
                    </Alert>
                    <Space h="md"/>
                  </>
                }
                <Box pos="relative">
                  <LoadingOverlay visible={loading}/>
                  <CompanyDisposalReportForm
                    defaultValues={{
                      ...(formDefaults ?? {}),
                      category: formDefaults?.category ?? appCategory
                    }}
                    options={{
                      regionId: regionId,
                      category: appCategory,
                    }}
                    submitting={loading}
                    onSubmit={onFormSubmit}/>
                </Box>
              </Paper>
            </Center>
          </Container>
        )}
      </ThemedPage>
    );
  }
;

